// src/pages/secret/SecretConsolePage.tsx
// The secrets console — seal status header, engine sidebar, per-engine-type workspace.
// See docs/ux/secret-ia.md section 1. Admin-gated (AdminRoute, in App.tsx).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useSecretSecretEngine } from '@react/hooks';
import { Button, Input } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { SealPanel } from '@/features/secret/SealPanel';
import { EngineSidebar } from '@/features/secret/EngineSidebar';
import { KvBrowser } from '@/features/secret/KvBrowser';
import { PkiPanel } from '@/features/secret/PkiPanel';
import { DatabasePanel } from '@/features/secret/DatabasePanel';
import { TransitPanel } from '@/features/secret/TransitPanel';
import { TypedConfirmDialog } from '@/features/secret/TypedConfirmDialog';
import type { SecretEngine } from '@core/types/secret';
import {
  engineTypeLabelKey, engineStatusVariant, engineStatusLabelKey, isKvEngine,
  engineConfirmPhrase,
} from '@/features/secret/logic';

function EngineSettingsForm({ engine, onSaved, onCancel }: { engine: SecretEngine; onSaved: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  const store = useSecretSecretEngine();
  const [defaultTtl, setDefaultTtl] = useState(String(engine.default_lease_ttl_seconds || ''));
  const [maxTtl, setMaxTtl] = useState(String(engine.max_lease_ttl_seconds || ''));
  const [sealWrap, setSealWrap] = useState(engine.seal_wrap);
  const [isSaving, setIsSaving] = useState(false);

  const engineId = engine.entity?.id ?? '';

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await store.TuneEngine(engineId, {
        engine_id: engineId,
        default_lease_ttl_seconds: defaultTtl ? parseInt(defaultTtl, 10) : undefined,
        max_lease_ttl_seconds: maxTtl ? parseInt(maxTtl, 10) : undefined,
        seal_wrap: sealWrap,
        update_mask: ['default_lease_ttl_seconds', 'max_lease_ttl_seconds', 'seal_wrap'],
      });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border-b border-border bg-muted/20 p-4 space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <Input label={t('secret.engine.field.defaultLeaseTtl')} type="number" min={0} value={defaultTtl} onChange={(e) => setDefaultTtl(e.target.value)} />
        <Input label={t('secret.engine.field.maxLeaseTtl')} type="number" min={0} value={maxTtl} onChange={(e) => setMaxTtl(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
        <input type="checkbox" checked={sealWrap} onChange={(e) => setSealWrap(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
        {t('secret.engine.field.sealWrap')}
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving}>{isSaving ? t('common.saving') : t('common.save')}</Button>
      </div>
    </div>
  );
}

function EngineWorkspace({ engine, onUnmounted }: { engine: SecretEngine; onUnmounted: () => void }) {
  const { t } = useI18n();
  const store = useSecretSecretEngine();
  const engineId = engine.entity?.id ?? '';
  const [showSettings, setShowSettings] = useState(false);
  const [unmountOpen, setUnmountOpen] = useState(false);
  const [isUnmounting, setIsUnmounting] = useState(false);
  const [tuneToken, setTuneToken] = useState(0);

  const doUnmount = async () => {
    setIsUnmounting(true);
    try {
      await store.UnmountEngine(engineId);
      setUnmountOpen(false);
      onUnmounted();
    } finally {
      setIsUnmounting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" key={tuneToken}>
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-foreground">{engine.name || engine.slug}</h1>
            <StatusBadge variant={engineStatusVariant(engine.status)} label={t(engineStatusLabelKey(engine.status))} size="sm" />
          </div>
          <p className="text-xs text-muted-foreground">{t(engineTypeLabelKey(engine.type))} · {engine.mount_path}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowSettings((v) => !v)}>{t('secret.engine.settings')}</Button>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setUnmountOpen(true)}>{t('secret.engine.unmount')}</Button>
        </div>
      </div>

      {showSettings && (
        <EngineSettingsForm engine={engine} onSaved={() => { setShowSettings(false); setTuneToken((n) => n + 1); }} onCancel={() => setShowSettings(false)} />
      )}

      <TypedConfirmDialog
        open={unmountOpen}
        title={t('secret.engine.unmountTitle')}
        description={t('secret.engine.unmountDescription')}
        phrase={engineConfirmPhrase(engine)}
        confirmLabel={t('secret.engine.unmount')}
        isConfirming={isUnmounting}
        onConfirm={doUnmount}
        onCancel={() => setUnmountOpen(false)}
      />

      {isKvEngine(engine.type) && <KvBrowser engineId={engineId} />}
      {engine.type === 'SECRET_ENGINE_TYPE_PKI' && <PkiPanel engineId={engineId} />}
      {engine.type === 'SECRET_ENGINE_TYPE_DATABASE' && <DatabasePanel engineId={engineId} />}
      {engine.type === 'SECRET_ENGINE_TYPE_TRANSIT' && <TransitPanel engineId={engineId} />}
      {!isKvEngine(engine.type) && engine.type !== 'SECRET_ENGINE_TYPE_PKI' && engine.type !== 'SECRET_ENGINE_TYPE_DATABASE' && engine.type !== 'SECRET_ENGINE_TYPE_TRANSIT' && (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">{t('secret.engine.unsupportedType')}</p>
        </div>
      )}
    </div>
  );
}

export const SecretConsolePage = observer(() => {
  const { t } = useI18n();
  const [selectedEngine, setSelectedEngine] = useState<SecretEngine | null>(null);
  const [sidebarToken, setSidebarToken] = useState(0);

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between px-6 py-3 border-b border-border">
        <h1 className="text-xl font-semibold text-foreground">{t('secret.console.title')}</h1>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/secrets/leases" className="text-primary hover:underline">{t('secret.console.leasesLink')}</Link>
          <Link to="/secrets/policies" className="text-primary hover:underline">{t('secret.console.policiesLink')}</Link>
          <Link to="/secrets/audit" className="text-primary hover:underline">{t('secret.console.auditLink')}</Link>
        </nav>
      </div>

      <SealPanel>
        <div className="flex flex-1 min-h-0">
          <EngineSidebar
            selectedEngineId={selectedEngine?.entity?.id ?? null}
            onSelect={setSelectedEngine}
            reloadToken={sidebarToken}
          />
          {selectedEngine ? (
            <EngineWorkspace
              key={selectedEngine.entity?.id}
              engine={selectedEngine}
              onUnmounted={() => { setSelectedEngine(null); setSidebarToken((n) => n + 1); }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <p className="text-sm text-muted-foreground">{t('secret.console.selectEngine')}</p>
            </div>
          )}
        </div>
      </SealPanel>
    </div>
  );
});

export default SecretConsolePage;
